import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { ScrollReveal, TextReveal, BandReveal, Pressable, InteractiveDots } from '@/components/motion'
import { ArrowRight, LucideIcon } from 'lucide-react'

interface CTAButton {
  label: string
  href: string
  variant?: 'primary' | 'secondary' | 'outline'
  icon?: LucideIcon
}

interface CTASectionProps {
  overline?: string
  title: string
  description?: string
  buttons?: CTAButton[]
  variant?: 'dark' | 'light' | 'accent' | 'gradient'
  align?: 'center' | 'left'
  className?: string
  /** Pointer-reactive dot field; the landing page only. */
  interactiveDots?: boolean
}

export function CTASection({
  overline,
  title,
  description,
  buttons = [],
  variant = 'dark',
  align = 'center',
  className,
  interactiveDots = false,
}: CTASectionProps) {
  const bgClasses = {
    dark: 'bg-[var(--color-landing-primary)] dark:bg-[var(--color-landing-black)]',
    light: 'bg-[var(--color-landing-gray-50)] dark:bg-[var(--color-landing-gray-900)]',
    accent: 'bg-[var(--color-landing-accent)] dark:bg-[var(--color-landing-accent)]',
    gradient:
      'bg-[linear-gradient(105deg,var(--color-landing-accent)_0%,var(--color-landing-secondary)_100%)]',
  }

  const textClasses = {
    dark: {
      overline: 'text-[var(--color-landing-gray-400)]',
      title: 'text-white',
      description: 'text-[var(--color-landing-gray-400)]',
    },
    light: {
      overline: 'text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]',
      title: 'text-[var(--color-landing-gray-900)] dark:text-white',
      description: 'text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]',
    },
    accent: {
      overline: 'text-white/70',
      title: 'text-white',
      description: 'text-white/80',
    },
    gradient: {
      overline: 'text-white/70',
      title: 'text-white',
      description: 'text-white/85',
    },
  }

  // Coloured bands get the white sweep; on the black band the solid
  // button inverts to white with the default blue sweep.
  const onColor = variant === 'accent' || variant === 'gradient' || variant === 'dark'
  const invertSolid = variant === 'dark'

  const renderButton = (button: CTAButton, index: number) => {
    const Icon = button.icon
    const outline = button.variant === 'secondary' || button.variant === 'outline'
    const link = (
      <Link
        key={index}
        to={button.href}
        className={cn(
          'landing-cta landing-cta--lg landing-btn-sweep',
          outline ? 'landing-cta--outline' : 'landing-cta--solid',
          onColor && (outline || !invertSolid) && 'landing-cta--on-color',
          !outline && invertSolid && '!bg-white !border-white !text-[var(--color-landing-primary)] hover:!text-white'
        )}
      >
        {Icon && <Icon className="h-5 w-5" />}
        {button.label}
        {!outline && <ArrowRight className="h-5 w-5" />}
      </Link>
    )
    return outline ? link : <Pressable key={index}>{link}</Pressable>
  }

  return (
    <BandReveal>
    <section
      className={cn(
        'landing-section-lg relative overflow-hidden',
        bgClasses[variant],
        className
      )}
    >
      {(variant === 'accent' || variant === 'gradient') && <InteractiveDots interactive={interactiveDots} />}
      <div className="landing-container relative z-10">
        <div
          className={cn(
            'max-w-3xl',
            align === 'center' && 'mx-auto text-center'
          )}
        >
          {overline && (
            <ScrollReveal delay={0.1}>
              <p className={cn('landing-text-overline mb-6', textClasses[variant].overline)}>
                {overline}
              </p>
            </ScrollReveal>
          )}

          <TextReveal
            as="h2"
            className={cn(
              variant === 'gradient' ? 'landing-text-display-xl' : 'landing-text-display-lg',
              'mb-5',
              textClasses[variant].title
            )}
          >
            {title}
          </TextReveal>

          {description && (
            <ScrollReveal delay={0.3}>
              <p
                className={cn(
                  'landing-text-body mb-10 max-w-xl',
                  align === 'center' && 'mx-auto',
                  textClasses[variant].description
                )}
              >
                {description}
              </p>
            </ScrollReveal>
          )}

          {buttons.length > 0 && (
            <ScrollReveal
              delay={0.4}
              className={cn(
                'flex flex-wrap gap-4',
                align === 'center' && 'justify-center'
              )}
            >
              {buttons.map(renderButton)}
            </ScrollReveal>
          )}
        </div>
      </div>
    </section>
    </BandReveal>
  )
}

export default CTASection
